// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import { advisoryApi } from '@shared/data-access/api/brokerage/advisory-api.v2';
import { useMemo, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { type Advisor } from '@shared/data-access/types/brokerage-types';
import { type LoaderStatus } from '../components/loader/loader';
import { selectAllAdvisors } from '@shared/data-access/slices/clients';

/**
 * Advisor row type for table display with hierarchical structure. Can represent
 * both advisor parent rows and account subrows.
 */
export interface AdvisorRow {
  id: number | string;
  email?: string;
  name: string;
  status?: string;
  subRows: AdvisorRow[];
  /** Indicates if this is the parent row that allows expansion */
  parent?: boolean;
  /** Account-specific fields (only present in account subrows) */
  institutionId?: string;
  institutionName?: string;
  iban?: string;
}

/**
 * Transforms Advisor data from the new advisory API to AdvisorRow format for table
 * display. The subRows now contain accounts instead of institutions.
 */
const buildAdvisorsTableData = (advisors: Advisor[]): AdvisorRow[] => {
  return advisors.map<AdvisorRow>(advisor => {
    // Flatten all accounts from all institutions
    const accounts: AdvisorRow[] = advisor.institutions.flatMap(institution =>
      institution.products.accounts.map(account => ({
        id: `${institution.id}-${account.iban}`,
        email: undefined,
        name: account.name || account.iban,
        status: account.status || undefined,
        institutionId: institution.id,
        institutionName: institution.name,
        iban: account.iban,
        subRows: [],
      })),
    );

    return {
      id: advisor.id,
      email: advisor.email,
      name: advisor.name || advisor.email,
      status: undefined,
      parent: true,
      subRows: accounts,
    };
  });
};

export const useAdvisors = () => {
  const advisorsQuery = advisoryApi.useGetAdvisoryUsersQuery();

  const advisors = useSelector(selectAllAdvisors);

  const advisorsData = useMemo(() => {
    if (advisors.length === 0) return [];
    return buildAdvisorsTableData(advisors);
  }, [advisors]);

  const status = useMemo<LoaderStatus>(() => {
    const isLoading = advisorsQuery.isLoading;
    const isError = advisorsQuery.isError;
    const isFetching = advisorsQuery.isFetching;

    return isLoading
      ? 'pending'
      : isError && !isFetching
      ? 'rejected'
      : isFetching
      ? 'fetching'
      : 'fulfilled';
  }, [advisorsQuery.isError, advisorsQuery.isFetching, advisorsQuery.isLoading]);

  const hasData = useMemo(() => {
    return advisors.length > 0;
  }, [advisors.length]);

  const onRefresh = useCallback(() => {
    advisorsQuery.refetch();
  }, [advisorsQuery]);

  return {
    advisors,
    advisorsData,
    advisorsQuery,
    status,
    hasData,
    onRefresh,
  };
};
